import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Property,
  PropertySensorGroupRow,
  PropertyWithSensorGroups,
  UpdatePropertyRequest,
} from '@metron/shared';
import { createLogger, Permission, toErrorMessage } from '@metron/shared';
import { useProperties } from '../../hooks/useProperties.ts';
import { useSensorGroups } from '../../hooks/useSensorGroups.ts';
import { usePermissions } from '../../hooks/usePermissions.ts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog.tsx';
import { Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { PropertyRow } from '../property/PropertyRow.tsx';
import { ImportPropertiesModal } from '../property/ImportPropertiesModal.tsx';

const logger = createLogger('CustomerPropertiesTab');

interface SensorGroupRowEdit extends PropertySensorGroupRow {
  isNew?: boolean;
  isDeleted?: boolean;
  tempId?: string; // For tracking new rows before they're saved
}

interface PropertyEdit extends Property {
  isNew?: boolean;
  isDeleted?: boolean;
  isDirty?: boolean;
  errors?: Record<string, string>;
  sensorGroups: SensorGroupRowEdit[]; // Array of sensor group rows
}

/**
 * Properties tab of the customer details page.
 *
 * Allows viewing, creating, editing, and deleting properties for a customer.
 * New properties are added to a shared state, but each property manages its own local state and saves independently.
 */
export function CustomerPropertiesTab({ customerId }: { customerId: number }) {
  const { hasPermission } = usePermissions();
  const {
    listProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    assignSensorGroup,
    unassignSensorGroups,
    addMasterChildRelationship,
    removeMasterChildRelationship,
  } = useProperties();
  const {
    sensorGroups: allSensorGroups,
    isLoading: sensorGroupsLoading,
    refetchSensorGroups,
    getEligibleForMasterRole,
    getEligibleForChildRole,
    getEligibleForStandaloneAssignment,
  } = useSensorGroups(customerId);

  const [properties, setProperties] = useState<PropertyEdit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Load properties with nested sensor group information (single API call)
  useEffect(() => {
    const loadData = async () => {
      if (!customerId || sensorGroupsLoading) return;

      setIsLoading(true);
      setError(null);

      try {
        // Load properties with nested sensor groups in a single request
        const { properties: loadedProperties } = await listProperties({
          customerId,
          limit: 1000,
        });

        // Properties now include sensorGroups array from the API
        setProperties(loadedProperties);
      } catch (err) {
        const errorMessage = toErrorMessage(err, 'Failed to load data');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [customerId, listProperties, sensorGroupsLoading]);

  const handleImportComplete = useCallback(async () => {
    // Reload properties after import
    if (!customerId) return;

    try {
      const { properties: loadedProperties } = await listProperties({
        customerId,
        limit: 1000,
      });
      setProperties(loadedProperties);
    } catch (err) {
      logger.error({ err }, 'Failed to reload properties');
    }
  }, [customerId, listProperties]);

  const handleAddProperty = useCallback(() => {
    const newProperty: PropertyEdit = {
      id: Date.now(), // Temporary ID for new properties
      name: '',
      customerId,
      metronId: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isNew: true,
      isDirty: true,
      errors: {},
      sensorGroups: [], // Start with no sensor groups
    };

    setProperties((prev) => [newProperty, ...prev]);
  }, [customerId]);

  const handlePropertySave = useCallback(
    async (id: number, data: UpdatePropertyRequest): Promise<PropertyWithSensorGroups> => {
      // Find the property to check if it's new
      const property = properties.find((p) => p.id === id);
      const isNew = property?.isNew || false;

      // Use createProperty for new properties, updateProperty for existing ones
      // Both now return PropertyWithSensorGroups
      const savedProperty = isNew
        ? await createProperty({
          name: data.name!,
          metronId: data.metronId!,
          customerId: property.customerId,
        })
        : await updateProperty(id, data);

      // Update local properties array with saved data
      setProperties((prev) => {
        const index = prev.findIndex((prop) => prop.id === id);
        if (index === -1) return prev;

        return [
          ...prev.slice(0, index),
          savedProperty,
          ...prev.slice(index + 1),
        ];
      });

      return savedProperty;
    },
    [properties, createProperty, updateProperty],
  );

  const handleMarkForDeletion = useCallback((id: number) => {
    setDeleteConfirmId(id);
  }, []);

  const handleCancelNewProperty = useCallback((id: number) => {
    setProperties((prev) => prev.filter((prop) => prop.id !== id));
  }, []);

  const handleAssignSensorGroup = useCallback(
    async (propertyId: number, sensorGroupId: number): Promise<void> => {
      try {
        const updatedProperty = await assignSensorGroup(propertyId, sensorGroupId);

        // Update local properties array with the updated property
        setProperties((prev) => {
          const index = prev.findIndex((prop) => prop.id === propertyId);
          if (index === -1) return prev;

          return [
            ...prev.slice(0, index),
            updatedProperty,
            ...prev.slice(index + 1),
          ];
        });

        // Invalidate sensor groups cache to refresh available groups
        await refetchSensorGroups();

        toast.success('Sensor group assigned successfully');
      } catch (err) {
        const errorMessage = toErrorMessage(err, 'Failed to assign sensor group');
        toast.error(errorMessage);
        throw err;
      }
    },
    [assignSensorGroup, refetchSensorGroups],
  );

  const handleStandaloneChange = useCallback(
    async (
      propertyId: number,
      oldSensorGroupId: number,
      newSensorGroupId: number | null,
    ): Promise<void> => {
      try {
        let updatedProperty: PropertyWithSensorGroups;

        if (newSensorGroupId === null) {
          // Remove assignment
          updatedProperty = await unassignSensorGroups(propertyId, [oldSensorGroupId]);
          toast.success('Sensor group unassigned successfully');
        } else {
          // Change to a different sensor group: unassign old, assign new
          await unassignSensorGroups(propertyId, [oldSensorGroupId]);
          updatedProperty = await assignSensorGroup(propertyId, newSensorGroupId);
          toast.success('Sensor group reassigned successfully');
        }

        // Update local properties array with the updated property
        setProperties((prev) => {
          const index = prev.findIndex((prop) => prop.id === propertyId);
          if (index === -1) return prev;

          return [
            ...prev.slice(0, index),
            updatedProperty,
            ...prev.slice(index + 1),
          ];
        });

        // Invalidate sensor groups cache to refresh available groups
        await refetchSensorGroups();
      } catch (err) {
        const errorMessage = err instanceof Error
          ? err.message
          : 'Failed to update sensor group assignment';
        toast.error(errorMessage);
        throw err;
      }
    },
    [assignSensorGroup, unassignSensorGroups, refetchSensorGroups],
  );

  const handleAddMaster = useCallback(
    async (
      propertyId: number,
      childSensorGroupId: number,
      newMasterId: number,
    ): Promise<void> => {
      try {
        const updatedProperty = await addMasterChildRelationship(
          propertyId,
          newMasterId,
          childSensorGroupId,
        );

        // Update local properties array with the updated property
        setProperties((prev) => {
          const index = prev.findIndex((prop) => prop.id === propertyId);
          if (index === -1) return prev;

          return [
            ...prev.slice(0, index),
            updatedProperty,
            ...prev.slice(index + 1),
          ];
        });

        // Invalidate sensor groups cache to refresh available groups
        await refetchSensorGroups();

        toast.success('Master assigned successfully');
      } catch (err) {
        const errorMessage = toErrorMessage(err, 'Failed to assign master');
        toast.error(errorMessage);
        throw err;
      }
    },
    [addMasterChildRelationship, refetchSensorGroups],
  );

  const handleRemoveMaster = useCallback(
    async (
      propertyId: number,
      masterSensorGroupId: number,
    ): Promise<void> => {
      try {
        const updatedProperty = await removeMasterChildRelationship(
          propertyId,
          masterSensorGroupId,
        );

        // Update local properties array with the updated property
        setProperties((prev) => {
          const index = prev.findIndex((prop) => prop.id === propertyId);
          if (index === -1) return prev;

          return [
            ...prev.slice(0, index),
            updatedProperty,
            ...prev.slice(index + 1),
          ];
        });

        // Invalidate sensor groups cache to refresh available groups
        await refetchSensorGroups();

        toast.success('Master removed successfully');
      } catch (err) {
        const errorMessage = toErrorMessage(err, 'Failed to remove master');
        toast.error(errorMessage);
        throw err;
      }
    },
    [removeMasterChildRelationship, refetchSensorGroups],
  );

  const handleChildChange = useCallback(
    async (
      propertyId: number,
      masterSensorGroupId: number,
      newChildId: number | null,
    ): Promise<void> => {
      try {
        let updatedProperty: PropertyWithSensorGroups;

        if (newChildId === null) {
          // Remove child (break relationship)
          updatedProperty = await removeMasterChildRelationship(
            propertyId,
            masterSensorGroupId,
          );
          toast.success('Child removed successfully');
        } else {
          // Add or change child
          updatedProperty = await addMasterChildRelationship(
            propertyId,
            masterSensorGroupId,
            newChildId,
          );
          toast.success('Child assigned successfully');
        }

        // Update local properties array with the updated property
        setProperties((prev) => {
          const index = prev.findIndex((prop) => prop.id === propertyId);
          if (index === -1) return prev;

          return [
            ...prev.slice(0, index),
            updatedProperty,
            ...prev.slice(index + 1),
          ];
        });

        // Invalidate sensor groups cache to refresh available groups
        await refetchSensorGroups();
      } catch (err) {
        const errorMessage = err instanceof Error
          ? err.message
          : 'Failed to update child assignment';
        toast.error(errorMessage);
        throw err;
      }
    },
    [addMasterChildRelationship, removeMasterChildRelationship, refetchSensorGroups],
  );

  const confirmDelete = useCallback(async () => {
    if (deleteConfirmId === null) return;

    try {
      await deleteProperty(deleteConfirmId);

      // Remove from local state
      setProperties((prev) => prev.filter((prop) => prop.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      toast.success('Property deleted successfully');
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to delete property');
      toast.error(errorMessage);
    }
  }, [deleteConfirmId, deleteProperty]);

  const visibleProperties = useMemo(() => {
    return properties;
  }, [properties]);

  const canEditProperties = hasPermission(Permission.EDIT_PROPERTIES);

  if (isLoading || sensorGroupsLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-end'>
        <div className='flex gap-2'>
          {canEditProperties && (
            <Button
              onClick={() => setShowImportModal(true)}
              variant='outline'
            >
              <Upload className='h-4 w-4 mr-2' />
              Import Properties
            </Button>
          )}
          <Button
            onClick={handleAddProperty}
            variant='outline'
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Property
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Properties</CardTitle>
          <CardDescription>
            Properties for this customer and their sensor group assignments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {visibleProperties.length === 0
            ? (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <p className='text-muted-foreground mb-4'>
                  No properties found for this customer.
                </p>
                <Button onClick={handleAddProperty} variant='outline'>
                  <Plus className='h-4 w-4 mr-2' />
                  Add First Property
                </Button>
              </div>
            )
            : (
              <div className='space-y-6'>
                {visibleProperties.map((property) => (
                  <PropertyRow
                    key={property.id}
                    property={property}
                    canEditProperties={canEditProperties}
                    allSensorGroups={allSensorGroups}
                    onPropertySave={handlePropertySave}
                    onMarkForDeletion={handleMarkForDeletion}
                    onCancelNewProperty={handleCancelNewProperty}
                    onAssignSensorGroup={handleAssignSensorGroup}
                    onStandaloneChange={handleStandaloneChange}
                    onAddMaster={handleAddMaster}
                    onRemoveMaster={handleRemoveMaster}
                    onChildChange={handleChildChange}
                    getEligibleForMasterRole={getEligibleForMasterRole}
                    getEligibleForChildRole={getEligibleForChildRole}
                    getEligibleForStandaloneAssignment={getEligibleForStandaloneAssignment}
                  />
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteConfirmId !== null}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Properties Modal */}
      <ImportPropertiesModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        customerId={customerId}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
}
